import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { frameworkLabel, scorePercent } from "../utils";
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_WHITE, outlinedButtonSx, primaryButtonSx } from "../theme";

const scoreLabel = (value) => {
  const percent = scorePercent(value);
  if (percent == null) return "Not yet scored";
  return `${Math.round(percent)}%`;
};

const AuditComplianceDialog = ({ open, status, audit, error, onClose, onOpenScope }) => {
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: AH_MUTED, fontWeight: 600 }}>
          {audit?.auditCode || "Audit"}
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: AH_TEXT }}>
          {status === "loading" ? "Loading audit" : audit?.name || "Compliance view"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {status === "loading" ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : status === "error" ? (
          <Typography sx={{ fontSize: 14, color: "#b91c1c", py: 2 }}>{error}</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1, pb: 1 }}>
            <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
              {audit?.ownerEmail ? `Owner ${audit.ownerEmail}` : "No owner"}
              {` · ${audit?.accountCount ?? accounts.length} account${(audit?.accountCount ?? accounts.length) === 1 ? "" : "s"}`}
              {` · ${audit?.scopeCount ?? 0} framework${audit?.scopeCount === 1 ? "" : "s"}`}
              {` · Audit score ${scoreLabel(audit?.auditScore)}`}
            </Typography>
            <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
              Choose an account and framework. The compliance page uses that scope&apos;s framework name, latest run, and account.
            </Typography>
            {accounts.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: AH_TEXT, py: 2 }}>This audit has no accounts.</Typography>
            ) : (
              accounts.map((account) => {
                const scopes = Array.isArray(account.scopes) ? account.scopes : [];
                const regions = Array.isArray(account.regions) ? account.regions.filter(Boolean) : [];
                const title = account.accountName || account.accountNumber;
                return (
                  <Box
                    key={account.auditAccountId || account.accountNumber}
                    sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "10px", backgroundColor: AH_WHITE, p: 1.5 }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: AH_TEXT }}>{title}</Typography>
                    <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>
                      {account.accountNumber}
                      {regions.length ? ` · ${regions.join(", ")}` : " · No region scope"}
                    </Typography>
                    {scopes.length === 0 ? (
                      <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 1 }}>No frameworks on this account.</Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.25 }}>
                        {scopes.map((scope) => {
                          const name = scope.framework?.name;
                          const canOpen = Boolean(name && scope.latestRunId);
                          return (
                            <Box
                              key={scope.auditScopeId}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT }}>
                                  {frameworkLabel(scope.framework)}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                                  {canOpen
                                    ? `Score ${scoreLabel(scope.auditScore)}`
                                    : "No completed scan for this framework yet."}
                                </Typography>
                              </Box>
                              <Button
                                disabled={!canOpen}
                                onClick={() => onOpenScope(account, scope)}
                                sx={{ ...primaryButtonSx, height: 34, minHeight: 34 }}
                              >
                                Open compliance
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
              <Button onClick={onClose} sx={outlinedButtonSx}>
                Close
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuditComplianceDialog;
